import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";

import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ConstructionIcon from "@mui/icons-material/Construction";
import BuildIcon from "@mui/icons-material/Build";
import BoltIcon from "@mui/icons-material/Bolt";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import LockResetIcon from "@mui/icons-material/LockReset";
import EmailIcon from "@mui/icons-material/Email";

const COLORS = {
  forest: "#14532D",
  deepForest: "#052E16",
  darkerGreen: "#022C16",
  green: "#15803D",
  emerald: "#16A34A",
  brightGreen: "#22C55E",
  freshGreen: "#4ADE80",
  softGreen: "#DCFCE7",
  paleGreen: "#F4FBF5",

  gold: "#D4A017",
  brightGold: "#FACC15",
  softGold: "#FEF3C7",

  black: "#07110A",
  charcoal: "#111827",
  white: "#FFFFFF",

  text: "#102216",
  muted: "#64756A",
  border: "#D8E8DC",

  red: "#B91C1C",
};

const VerifyCode = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { email } = location.state || {};

  useEffect(() => {
    if (!email) {
      setMessage(
        "No email found. Please return and request a new verification code."
      );
    }
  }, [email]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (code.length !== 6) {
      setMessage("Please enter the complete 6-digit code.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.post("/verify-reset-code/", {
        email,
        code,
      });

      setMessage(res.data.message || "Verification successful.");

      setTimeout(() => {
        navigate("/resetPassword", {
          state: { email, code },
        });
      }, 1000);
    } catch (err) {
      setMessage(
        err.response?.data?.error ||
          "Invalid verification code."
      );
    } finally {
      setLoading(false);
    }
  };

  const technicalIcons = [
    {
      Icon: EngineeringIcon,
      top: "11%",
      left: "9%",
      size: 34,
      delay: 0,
    },
    {
      Icon: ConstructionIcon,
      top: "19%",
      right: "10%",
      size: 30,
      delay: 0.7,
    },
    {
      Icon: BuildIcon,
      bottom: "17%",
      left: "10%",
      size: 32,
      delay: 1.2,
    },
    {
      Icon: PrecisionManufacturingIcon,
      bottom: "11%",
      right: "10%",
      size: 36,
      delay: 1.8,
    },
    {
      Icon: BoltIcon,
      top: "43%",
      left: "4%",
      size: 25,
      delay: 2.2,
    },
    {
      Icon: BuildIcon,
      top: "47%",
      right: "5%",
      size: 25,
      delay: 2.8,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, md: 5 },

        background: `
          radial-gradient(
            circle at 15% 20%,
            rgba(34,197,94,.12),
            transparent 30%
          ),
          radial-gradient(
            circle at 85% 80%,
            rgba(212,160,23,.11),
            transparent 28%
          ),
          linear-gradient(
            135deg,
            #F8FCF9 0%,
            #F4FBF5 45%,
            #EEF8F0 100%
          )
        `,

        "@keyframes floatOne": {
          "0%, 100%": {
            transform: "translate3d(0,0,0) scale(1)",
          },
          "50%": {
            transform: "translate3d(25px,-35px,0) scale(1.05)",
          },
        },

        "@keyframes floatTwo": {
          "0%, 100%": {
            transform: "translate3d(0,0,0)",
          },
          "50%": {
            transform: "translate3d(-30px,30px,0)",
          },
        },

        "@keyframes pulseGlow": {
          "0%, 100%": {
            opacity: 0.35,
            transform: "scale(1)",
          },
          "50%": {
            opacity: 0.7,
            transform: "scale(1.15)",
          },
        },

        "@keyframes rotateSlow": {
          "0%": {
            transform: "rotate(0deg)",
          },
          "100%": {
            transform: "rotate(360deg)",
          },
        },

        "@keyframes gridMove": {
          "0%": {
            backgroundPosition: "0 0",
          },
          "100%": {
            backgroundPosition: "50px 50px",
          },
        },
      }}
    >
      {/* Decorative background glow */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 220, md: 420 },
          height: { xs: 220, md: 420 },
          borderRadius: "50%",
          background: `rgba(34,197,94,.10)`,
          filter: "blur(4px)",
          top: "-120px",
          left: "-100px",
          animation: "floatOne 15s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 240, md: 450 },
          height: { xs: 240, md: 450 },
          borderRadius: "50%",
          background: `rgba(212,160,23,.10)`,
          filter: "blur(6px)",
          bottom: "-150px",
          right: "-120px",
          animation: "floatTwo 18s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />

      {/* Technical grid */}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.22,
          pointerEvents: "none",
          backgroundImage: `
            linear-gradient(
              rgba(20,83,45,.08) 1px,
              transparent 1px
            ),
            linear-gradient(
              90deg,
              rgba(20,83,45,.08) 1px,
              transparent 1px
            )
          `,
          backgroundSize: "50px 50px",
          animation: "gridMove 25s linear infinite",
          maskImage:
            "linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)",
        }}
      />

      {/* Gold orbit */}

      <Box
        sx={{
          position: "absolute",
          width: { xs: 300, md: 520 },
          height: { xs: 300, md: 520 },
          borderRadius: "50%",
          border: "1px solid rgba(212,160,23,.15)",
          animation: "rotateSlow 35s linear infinite",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 240, md: 420 },
          height: { xs: 240, md: 420 },
          borderRadius: "50%",
          border: "1px dashed rgba(21,128,61,.18)",
          animation: "rotateSlow 28s linear infinite reverse",
          pointerEvents: "none",
        }}
      />

      {/* Floating vocational icons */}

      {technicalIcons.map(
        ({ Icon, delay, size, ...position }, index) => (
          <motion.div
            key={index}
            animate={{
              y: [0, -12, 0],
              rotate: [0, index % 2 === 0 ? 5 : -5, 0],
              opacity: [0.35, 0.7, 0.35],
            }}
            transition={{
              duration: 4 + index * 0.4,
              repeat: Infinity,
              delay,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              ...position,
              zIndex: 0,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "rgba(255,255,255,.72)",
                border:
                  "1px solid rgba(21,128,61,.14)",
                boxShadow:
                  "0 10px 30px rgba(20,83,45,.08)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Icon
                sx={{
                  fontSize: size,
                  color:
                    index % 2 === 0
                      ? COLORS.green
                      : COLORS.gold,
                }}
              />
            </Box>
          </motion.div>
        )
      )}

      {/* Floating particles */}

      {[...Array(30)].map((_, index) => (
        <motion.div
          key={`particle-${index}`}
          animate={{
            y: [0, -15, 0],
            opacity: [0.15, 0.65, 0.15],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 3 + (index % 5),
            repeat: Infinity,
            delay: index * 0.15,
            ease: "easeInOut",
          }}
          style={{
            position: "absolute",
            width: `${2 + (index % 3)}px`,
            height: `${2 + (index % 3)}px`,
            borderRadius: "50%",
            background:
              index % 3 === 0
                ? COLORS.green
                : index % 3 === 1
                ? COLORS.gold
                : COLORS.brightGreen,
            top: `${(index * 17) % 100}%`,
            left: `${(index * 29) % 100}%`,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Center content */}

      <motion.div
        initial={{
          opacity: 0,
          y: 50,
          scale: 0.97,
        }}
        animate={{
          opacity: 1,
          y: 0,
          scale: 1,
        }}
        transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{
          width: "100%",
          maxWidth: "560px",
          zIndex: 5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            p: {
              xs: 3,
              sm: 4,
              md: 5,
            },
            borderRadius: {
              xs: "24px",
              md: "30px",
            },
            background:
              "rgba(255,255,255,.94)",
            backdropFilter: "blur(24px)",
            border:
              "1px solid rgba(20,83,45,.12)",
            boxShadow: `
              0 30px 80px rgba(5,46,22,.12),
              0 8px 30px rgba(20,83,45,.07)
            `,
          }}
        >
          {/* Top brand line */}

          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: `linear-gradient(
                90deg,
                ${COLORS.deepForest},
                ${COLORS.green},
                ${COLORS.brightGreen},
                ${COLORS.gold},
                ${COLORS.deepForest}
              )`,
            }}
          />

          {/* Brand */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.8,
                borderRadius: "999px",
                background: COLORS.paleGreen,
                border:
                  `1px solid ${COLORS.border}`,
              }}
            >
              <EngineeringIcon
                sx={{
                  fontSize: 19,
                  color: COLORS.green,
                }}
              />

              <Typography
                sx={{
                  fontSize: {
                    xs: ".67rem",
                    sm: ".72rem",
                  },
                  fontWeight: 800,
                  letterSpacing: ".12em",
                  color: COLORS.forest,
                }}
              >
                IGNITE S • TRADE TRAINING INSTITUTE
              </Typography>
            </Box>
          </Box>

          {/* Verification icon */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <motion.div
              animate={{
                y: [0, -9, 0],
                rotate: [0, 2, -2, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: {
                    xs: 88,
                    md: 102,
                  },
                  height: {
                    xs: 88,
                    md: 102,
                  },
                  borderRadius: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `
                    linear-gradient(
                      145deg,
                      ${COLORS.deepForest},
                      ${COLORS.green}
                    )
                  `,
                  boxShadow: `
                    0 18px 40px rgba(5,46,22,.22)
                  `,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 7,
                    borderRadius: "22px",
                    border:
                      "1px solid rgba(255,255,255,.22)",
                  }}
                />

                <VerifiedUserIcon
                  sx={{
                    fontSize: {
                      xs: 48,
                      md: 56,
                    },
                    color: COLORS.white,
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    background: COLORS.brightGold,
                    top: 8,
                    right: 8,
                    boxShadow:
                      `0 0 20px ${COLORS.brightGold}`,
                    animation:
                      "pulseGlow 2s ease-in-out infinite",
                  }}
                />
              </Box>
            </motion.div>
          </Box>

          {/* Heading */}

          <Box textAlign="center">
            <Typography
              sx={{
                fontSize: {
                  xs: "1.8rem",
                  sm: "2.15rem",
                  md: "2.35rem",
                },
                fontWeight: 900,
                lineHeight: 1.1,
                letterSpacing: "-.035em",
                color: COLORS.deepForest,
              }}
            >
              Verify Reset Code
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 1.4,
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 54,
                  height: 4,
                  borderRadius: 999,
                  background: `linear-gradient(
                    90deg,
                    ${COLORS.green},
                    ${COLORS.gold}
                  )`,
                }}
              />
            </Box>

            <Typography
              sx={{
                color: COLORS.muted,
                fontSize: {
                  xs: ".9rem",
                  sm: ".95rem",
                },
                lineHeight: 1.7,
                maxWidth: 430,
                mx: "auto",
              }}
            >
              Enter the 6-digit verification code sent to
              your training portal email address.
            </Typography>

            {email && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  px: 2,
                  py: 1,
                  borderRadius: "12px",
                  background: COLORS.paleGreen,
                  border:
                    `1px solid ${COLORS.border}`,
                  maxWidth: "100%",
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 18,
                    color: COLORS.green,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: ".86rem",
                    fontWeight: 750,
                    color: COLORS.forest,
                    wordBreak: "break-all",
                  }}
                >
                  {email}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Security notice */}

          <Alert
            icon={
              <LockResetIcon
                sx={{
                  color: COLORS.green,
                }}
              />
            }
            severity="info"
            sx={{
              mt: 3.5,
              borderRadius: "16px",
              background: COLORS.paleGreen,
              border:
                `1px solid ${COLORS.border}`,
              color: COLORS.text,
              "& .MuiAlert-message": {
                fontSize: ".86rem",
                lineHeight: 1.6,
              },
            }}
          >
            Your verification code expires after 10 minutes.
            Enter it exactly as received to continue securely.
          </Alert>

          {/* Form */}

          <Box
            component="form"
            onSubmit={handleVerify}
            sx={{
              mt: 3,
            }}
          >
            <TextField
              fullWidth
              required
              label="6-Digit Verification Code"
              value={code}
              disabled={loading}
              autoComplete="one-time-code"
              inputMode="numeric"
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
              }}
              onChange={(e) => {
                const value =
                  e.target.value.replace(/\D/g, "");

                setCode(value);
              }}
              InputAdornmentProps={{
                position: "start",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockResetIcon
                      sx={{
                        color: COLORS.green,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,

                "& .MuiInputLabel-root": {
                  color: COLORS.muted,
                  fontWeight: 600,
                },

                "& .MuiInputLabel-root.Mui-focused": {
                  color: COLORS.green,
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  background: "#FFFFFF",
                  transition: "all .25s ease",

                  "& fieldset": {
                    borderColor: COLORS.border,
                  },

                  "&:hover fieldset": {
                    borderColor:
                      "rgba(21,128,61,.45)",
                  },

                  "&.Mui-focused": {
                    boxShadow:
                      "0 0 0 4px rgba(34,197,94,.10)",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.green,
                    borderWidth: "1.5px",
                  },
                },

                "& input": {
                  fontSize: {
                    xs: "1.25rem",
                    sm: "1.45rem",
                  },
                  fontWeight: 800,
                  letterSpacing: ".42em",
                  color: COLORS.deepForest,
                  textAlign: "center",
                  paddingLeft: "14px",
                },
              }}
            />

            {/* Verify button */}

            <motion.div
              whileHover={{
                scale: loading ? 1 : 1.015,
              }}
              whileTap={{
                scale: loading ? 1 : 0.985,
              }}
            >
              <Button
                fullWidth
                type="submit"
                disabled={loading}
                startIcon={
                  !loading ? (
                    <VerifiedUserIcon />
                  ) : null
                }
                sx={{
                  minHeight: 58,
                  borderRadius: "16px",
                  fontSize: ".96rem",
                  fontWeight: 800,
                  letterSpacing: ".01em",
                  color: COLORS.white,

                  background: `
                    linear-gradient(
                      135deg,
                      ${COLORS.deepForest} 0%,
                      ${COLORS.green} 55%,
                      ${COLORS.emerald} 100%
                    )
                  `,

                  boxShadow:
                    "0 14px 30px rgba(21,128,61,.22)",

                  transition:
                    "all .25s ease",

                  "&:hover": {
                    background: `
                      linear-gradient(
                        135deg,
                        ${COLORS.forest},
                        ${COLORS.emerald}
                      )
                    `,
                    boxShadow:
                      "0 18px 36px rgba(21,128,61,.28)",
                  },

                  "&:disabled": {
                    color: "rgba(255,255,255,.85)",
                    background:
                      "linear-gradient(135deg,#4B6B57,#5F806B)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: COLORS.white,
                    }}
                  />
                ) : (
                  "Verify Code"
                )}
              </Button>
            </motion.div>

            {/* Back button */}

            <Button
              fullWidth
              type="button"
              startIcon={<ArrowBackIcon />}
              disabled={loading}
              onClick={() =>
                navigate("/forgotPassword")
              }
              sx={{
                mt: 1.5,
                minHeight: 48,
                borderRadius: "14px",
                color: COLORS.forest,
                fontWeight: 700,

                "&:hover": {
                  background:
                    COLORS.paleGreen,
                  color: COLORS.green,
                },
              }}
            >
              Back to Password Recovery
            </Button>
          </Box>

          {/* Message */}

          {message && (
            <motion.div
              initial={{
                opacity: 0,
                y: 8,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.3,
              }}
            >
              <Box
                sx={{
                  mt: 2.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: "14px",
                  background:
                    message.toLowerCase().includes("successful")
                      ? COLORS.softGreen
                      : "#FEF2F2",
                  border:
                    message.toLowerCase().includes("successful")
                      ? "1px solid #BBE7C7"
                      : "1px solid #FECACA",
                }}
              >
                <Typography
                  textAlign="center"
                  sx={{
                    fontSize: ".86rem",
                    lineHeight: 1.5,
                    fontWeight: 700,
                    color:
                      message
                        .toLowerCase()
                        .includes("successful")
                        ? COLORS.forest
                        : COLORS.red,
                  }}
                >
                  {message}
                </Typography>
              </Box>
            </motion.div>
          )}

          {/* Footer */}

          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop:
                `1px solid ${COLORS.border}`,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: ".74rem",
                fontWeight: 800,
                letterSpacing: ".12em",
                color: COLORS.forest,
              }}
            >
              IGNITE S VOCATIONAL SCHOOL
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                fontSize: ".7rem",
                fontWeight: 700,
                letterSpacing: ".08em",
                color: COLORS.gold,
              }}
            >
              SKILLS • INNOVATION • EXCELLENCE
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: ".72rem",
                color: COLORS.muted,
              }}
            >
              Learn a Skill • Build a Future
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default VerifyCode;